import { $API } from "boot/api";

const isPaid = (p) => !!p && !p.is_free && Number(p.price ?? p.amount ?? 0) > 0;

export function normalizePlan(p) {
  if (
    p.is_free_trial ||
    p.free_trial_length != null ||
    p.trial_length != null
  ) {
  }
  const hub =
    Array.isArray(p.hub_addons) && p.hub_addons.length ? p.hub_addons[0] : null;
  const hub_code = hub?.code ?? null;

  const HUB_META = {
    "data-tools": {
      category: "analytics",
      features: ["Standard Reports", "Basic Insights"],
    },
    "fulfillment-hub": {
      category: "operations",
      features: ["Bulk Ops", "Labeling"],
    },
  };
  const meta = hub_code && HUB_META[hub_code] ? HUB_META[hub_code] : {};

  // coerce price/amount (null -> 0)
  const coerceNum = (v) => (v == null ? 0 : Number(v));

  // unify trial fields
  const raw_is_free_trial = Boolean(p.is_free_trial);
  const raw_trial_len = p.trial_length ?? p.free_trial_length ?? null;
  const raw_trial_per = p.trial_period ?? p.free_trial_period ?? null;

  const trial_length_num = raw_trial_len == null ? null : Number(raw_trial_len);
  const trial_period_norm = raw_trial_per
    ? String(raw_trial_per).toLowerCase()
    : null;

  // has_trial if backend says it's a free trial OR if there is a positive trial length+period
  const has_trial =
    p.has_trial === true ||
    raw_is_free_trial ||
    (Number.isFinite(trial_length_num) &&
      trial_length_num > 0 &&
      !!trial_period_norm);

  // per-type rates
  const per_type_rates = {};
  (p.rates_by_user || []).forEach((r) => {
    const key = String(r?.type || "")
      .trim()
      .toLowerCase();
    const amt = Number(r?.amount);
    if (key && Number.isFinite(amt)) per_type_rates[key] = amt;
  });

  return {
    ...p,
    // type code
    type_code: p?.type?.code ?? p.type_code,

    // price preferred over amount; both coerced to number
    price: coerceNum(p.price ?? p.amount),

    // recurrence flags unified
    is_recurring_billing:
      p.is_recurring_billing != null
        ? Boolean(p.is_recurring_billing)
        : String(p.billing_frequency || "").toLowerCase() === "recurring",
    billing_recurrence: p.billing_recurrence ?? null,
    billing_frequency: p.billing_frequency ?? null,

    // hub meta
    hub_code,
    category: p.category ?? meta.category ?? null,
    features: p.features ?? meta.features ?? [],

    // charges
    has_additional_charges:
      p.has_additional_charges ?? !!p.is_additional_charges,
    additional_charges: p.additional_charges ?? null,

    // trials (the fields the UI actually reads)
    has_trial,
    trial_length: has_trial ? trial_length_num ?? null : null,
    trial_period: has_trial ? trial_period_norm ?? null : null,

    // keep ai flag if exists
    ai_features: p.ai_features ?? false,

    // per-user pricing map
    per_type_rates,
  };
}

export function planHasSellingFees(p) {
  if (!p?.has_additional_charges) return false;
  const ch = p.additional_charges || {};
  // If any fee is a positive number, treat as a selling-fee plan
  return Object.values(ch).some((v) => v && Number(v.amount) > 0);
}

export function mapUserTypeToRateKey(userType) {
  // backend uses 'retail'|'cultivator'|'manufacturer'
  const map = {
    retailer: "retail",
    cultivator: "cultivator",
    manufacturer: "manufacturer",
  };
  return map[userType] || null;
}

function requiresPaymentForPlan(plan) {
  return isPaid(plan);
}

function requiresPaymentForSelection({ base, hubs, data }) {
  if (isPaid(base)) return true;
  if (isPaid(data)) return true;
  if (Array.isArray(hubs) && hubs.some(isPaid)) return true;
  return false;
}

export async function hydratePricing({ commit, getters, state }) {
  commit("SET_LOADING", true);
  try {
    const [{ data }, companyRes, pmRes] = await Promise.all([
      $API.CURRENT_COMPANY_PRICING_PLANS.GET(),
      $API.CURRENT_COMPANY.GET(),
      $API.CURRENT_COMPANY_PAYMENT_METHODS.GET(),
    ]);

    const allPlansNorm = (data.all_plans || []).map(normalizePlan);
    const companyPlansNorm = (data.company_plans || []).map(normalizePlan);

    commit("SET_ALL_PLANS", allPlansNorm);
    commit("SET_COMPANY_PLANS", companyPlansNorm);
    commit("SET_TRIAL_ALLOWED", data.is_trial_allowed);

    const hasMembership = companyPlansNorm.some(
      (p) => (p.type?.code || p.type_code) === "membership"
    );
    if (hasMembership) {
      localStorage.setItem("onboarding_completed", "true");
    }

    const licenses = companyRes?.data?.company?.licenses || [];
    const derivedType = licenses.some((l) => l.is_retailer)
      ? "retail"
      : licenses.some((l) => l.is_manufacturer)
      ? "manufacturer"
      : licenses.some((l) => l.is_cultivator)
      ? "cultivator"
      : null;
    commit("SET_COMPANY_TYPE", derivedType);

    const pms = (pmRes?.data?.payment_methods || []).map((pm) => ({
      id: pm.id,
      card_brand: pm.card_brand,
      card_ending: pm.card_ending,
      card_expires: pm.card_expires || null,
      gateway_type: pm.gateway_type || null,
      billing: pm.billing || null,
    }));
    commit("SET_PAYMENT_METHODS", pms);

    // Force Step 2 if there is NO user-type gating - TODO: need to rework
    if (!getters.hasTypeGating && state.currentStep === 1) {
      commit("SET_STEP", 2);
    }
  } catch (error) {
    console.error("Failed to hydrate pricing:", error);
    // Even on error, commit empty arrays so UI knows data is "loaded"
    commit("SET_ALL_PLANS", []);
    commit("SET_COMPANY_PLANS", []);
    throw error; // Re-throw so caller knows it failed
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function startPlan({ state, getters, dispatch, commit }) {
  commit("SET_LOADING", true);
  try {
    const all = state.allPlans || [];

    const base = state.basePlanId
      ? all.find((p) => p.id === state.basePlanId)
      : null;
    const hubs = (state.hubPlanIds || [])
      .map((id) => all.find((p) => p.id === id))
      .filter(Boolean);
    const data = state.dataPlanId
      ? all.find((p) => p.id === state.dataPlanId)
      : null;
    const paymentRequired = requiresPaymentForSelection(
      { base, hubs, data },
      state.isTrialAllowed
    );

    // preflight: only enforce PM if actually required
    if (paymentRequired && !state.paymentMethodId) {
      throw new Error("Add a payment method to continue.");
    }

    if (state.basePlanId) {
      const hubIds = Array.from(
        new Set([
          ...state.hubPlanIds,
          ...(state.dataPlanId ? [state.dataPlanId] : []),
        ])
      );
      await $API.CURRENT_COMPANY_PRICING_PLANS.POST("start", {
        plan: state.basePlanId,
        hubs: hubIds,
        payment_method: paymentRequired ? Number(state.paymentMethodId) : null,
      });
    }

    localStorage.setItem("onboarding_completed", "true");

    await dispatch("hydratePricing");
  } finally {
    commit("SET_LOADING", false);
  }
}

export default {
  hydratePricing,
  startPlan,
};
